'use client';

import { Suspense, useMemo } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';

import { appNavigate, shouldReleasePopupBeforeNavigate } from '@/utils/navigation/app-navigate';
import { withDyeQuery } from '@/utils/router/with-dye-query';

import type { ComponentProps, ReactNode } from 'react';

type TNextLinkProps = ComponentProps<typeof Link>;

export interface IAppLinkProps extends Omit<TNextLinkProps, 'href' | 'onNavigate'> {
    /** 站内路径（字符串）；会自动合并当前页落地相关五参 */
    href: string;
    /** 是否透传落地相关五参，默认 true */
    preserveDye?: boolean;
    /**
     * 跳过活动弹窗释放（同 path scrub 等）。
     * 默认：目标 scene 变化且有 current 时会先 release。
     */
    skipActivityRelease?: boolean;
    children?: ReactNode;
}

function AppLinkInner(props: IAppLinkProps) {
    const { href, preserveDye = true, skipActivityRelease, children, onClick, ...rest } = props;
    const searchParams = useSearchParams();
    const router = useRouter();

    const mergedHref = useMemo(() => {
        if (!preserveDye) {
            return href;
        }
        return withDyeQuery(href, searchParams);
    }, [href, preserveDye, searchParams]);

    return (
        <Link
            {...rest}
            href={mergedHref}
            onClick={onClick}
            onNavigate={e => {
                if (skipActivityRelease) {
                    return;
                }
                if (!shouldReleasePopupBeforeNavigate(mergedHref)) {
                    return;
                }
                // 拦截默认导航，改为先 release 再 push/replace（href 已合并染色，不再二次合并）
                e.preventDefault();
                const type = rest.replace === true ? 'replace' : 'push';
                appNavigate(router, mergedHref, {
                    type,
                    skipActivityRelease,
                    preserveDye: false,
                });
            }}
        >
            {children}
        </Link>
    );
}

/**
 * 声明式导航：next/link + 默认透传落地五参；跨 scene 时先 release 活动弹窗再跳转。
 * 内部 Suspense + useSearchParams，避免静态导出 / SSR 与客户端 href 不一致。
 */
export default function AppLink(props: IAppLinkProps) {
    const { href, preserveDye = true, skipActivityRelease, children, ...rest } = props;

    return (
        <Suspense
            fallback={
                <Link href={href} {...rest}>
                    {children}
                </Link>
            }
        >
            <AppLinkInner href={href} preserveDye={preserveDye} skipActivityRelease={skipActivityRelease} {...rest}>
                {children}
            </AppLinkInner>
        </Suspense>
    );
}
